import React, { useEffect } from 'react';
import SectionHead from '../components/ui/SectionHead.jsx';

const EDITOR_IN_CHIEF = {
  name: 'Dr Sugandha Jauhari',
  credentials: 'MD, Assistant Professor, Department of Community Medicine and Public Health, KGMU, Lucknow, India',
  role: 'Editor-in-Chief',
  emails: ['sugandha0488@gmail.com', 'editor@jamsd.com'],
  initials: 'SJ',
  photo: '/images/editorial/sugandha-jauhari.jpg',
};

const DIRECTOR = {
  name: 'Dr Mohit K Srivastava',
  credentials: 'MD, DNB, DM (Pain Medicine), MNAMS, Assistant Professor, Department of Physical Medicine and Rehabilitation, VMMC & SJH, New Delhi',
  role: 'Director — Editorial Operations',
  emails: ['mohitsri87@gmail.com'],
  initials: 'MS',
  photo: '/images/editorial/mohit-srivastava.png',
};

const EDITORIAL_BOARD = [
  {
    id: 1,
    name: 'Dr Shubham Dadoo',
    credentials: 'MBBS, MS (ENT), Fellow, Endoscopic Skull Base and Sinus Surgery, Associate Professor, Department of ENT, NCR-IMS Meerut, Director, Department of ENT–HNS, Optima Hospital, Meerut',
    email: 'dadoo_shubham@yahoo.com',
    initials: 'SD',
  },
  {
    id: 2,
    name: 'Dr Akanksha Srivastava',
    credentials: 'MBBS, MD (Pediatrics) (Gold Medalist, KGMU), Certified Adolescent Health Specialist (NHS UK & NPTEL–IIT Kharagpur), Gold Rank Holder — NPTEL',
    email: 'akankshadsrivastava511@gmail.com',
    initials: 'AS',
  },
  {
    id: 3,
    name: 'Dr Priyanka Bhardwaj',
    credentials: 'MBBS, MD (Psychiatry), Associate Professor & Head of Department, MEU Coordinator',
    email: 'priyankagmcdausa2025b@gmail.com',
    initials: 'PB',
  },
  {
    id: 4,
    name: 'Dr Mallicka',
    credentials: 'MD (Community Medicine), PhD, Associate Professor, Integral Institute of Medical Sciences, Lucknow, Uttar Pradesh',
    email: 'drmallicka09@gmail.com',
    initials: 'M',
  },
  {
    id: 5,
    name: 'Dr Manish Kumar',
    credentials: 'MBBS, MD (Microbiology), DIPC, CICP, CCEBDM, Assistant Professor, Department of Microbiology, Government Doon Medical College, Dehradun, Uttarakhand, Member, Infection Control Academy of India (IFCAI), Life Member, IAMM UP–UK Chapter, Life Member, Hospital Infection Society of India, Lucknow Chapter',
    email: 'drmanish1691@gmail.com',
    initials: 'MK',
  },
  {
    id: 6,
    name: 'Dr Shiva Tiwari',
    credentials: 'MBBS, PgDip Endocrinology, MBA (HAHM), FIOG, CCEBDM, CCMTD, CCGDM, Consultant — Endocrinology, Director, SP & SS Medical Research Support Services LLP',
    email: 'shiva.libra05@gmail.com',
    initials: 'ST',
  },
];

const MANAGING_TEAM = [
  {
    id: 1,
    name: 'Anya Vats, MA',
    role: 'Chief Managing Editor',
    org: 'JAMSD Publications, Lucknow, India',
    email: 'admin@jamsd.com',
    initials: 'AV',
    photo: '/images/editorial/anya-vats.jpg',
  },
  {
    id: 2,
    name: 'Aryan Singh',
    role: 'Editorial Assistant and IT Support',
    org: '',
    email: 'support@jamsd.com',
    initials: 'AS',
  },
];

function AvatarCircle({ initials = 'ED', large = false, photo, name = '' }) {
  const base = large
    ? 'w-24 h-24 text-3xl'
    : 'w-16 h-16 text-xl';
  if (photo) {
    return (
      <img
        src={photo}
        alt={name}
        className={`${base} rounded-full object-cover object-top flex-shrink-0 bg-slate-pale`}
      />
    );
  }
  return (
    <div
      className={`${base} rounded-full bg-slate-pale flex items-center justify-center font-display font-bold text-slate flex-shrink-0`}
      aria-hidden="true"
    >
      {initials}
    </div>
  );
}

function LeaderCard({ person, badgeLabel }) {
  return (
    <div className="bg-white rounded-xl border-2 border-amber shadow-md p-6 md:p-8 max-w-2xl">
      <div className="flex flex-col sm:flex-row items-start sm:items-center gap-6">
        <AvatarCircle initials={person.initials} photo={person.photo} name={person.name} large />
        <div className="flex-1">
          <span className="bg-amber text-white text-xs font-sans font-bold px-3 py-1 rounded-full uppercase tracking-wide">
            {badgeLabel}
          </span>
          <h2 className="font-display text-navy text-xl font-bold mt-2">{person.name}</h2>
          <p className="font-sans text-gray-600 text-sm mt-1.5 leading-relaxed">{person.credentials}</p>
          <div className="flex flex-wrap gap-x-4 gap-y-1 mt-3">
            {person.emails.map((email) => (
              <a key={email} href={`mailto:${email}`} className="font-sans text-amber font-semibold text-sm hover:text-amber-dark transition-colors">
                {email}
              </a>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

function EditorialBoard() {
  useEffect(() => {
    document.title = 'Editorial Board — JAMSD';
  }, []);

  return (
    <>
      {/* Page hero */}
      <section className="bg-navy pt-24 pb-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex gap-4 items-start">
            <div className="w-1 bg-amber rounded-full self-stretch" aria-hidden="true" />
            <div>
              <p className="font-sans text-amber text-xs font-semibold uppercase tracking-widest mb-2">
                Journal Leadership
              </p>
              <h1 className="font-display text-white text-3xl md:text-4xl font-bold mb-2">
                Editorial Board
              </h1>
              <p className="font-sans text-slate-light text-base max-w-2xl">
                Meet the dedicated experts guiding the scientific quality and direction of JAMSD.
              </p>
            </div>
          </div>
        </div>
      </section>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Editor-in-Chief */}
        <div className="mb-10">
          <SectionHead title="Editor-in-Chief" />
          <LeaderCard person={EDITOR_IN_CHIEF} badgeLabel="Editor-in-Chief" />
        </div>

        {/* Director - Editorial Operations */}
        <div className="mb-10">
          <SectionHead title="Director — Editorial Operations" />
          <LeaderCard person={DIRECTOR} badgeLabel="Director — Editorial Operations" />
        </div>

        {/* Editorial Board */}
        <div className="mb-10">
          <SectionHead title="Editorial Board" />
          <p className="font-sans text-gray-600 text-sm leading-relaxed mb-5 max-w-3xl">
            The Editorial Board provides strategic guidance, ensures editorial independence, and supports the
            journal&#x2019;s global mission.
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {EDITORIAL_BOARD.map((editor) => (
              <div
                key={editor.id}
                className="bg-white rounded-lg border border-bg-border shadow-sm p-5 flex items-start gap-4 hover:shadow-md transition-shadow"
              >
                <AvatarCircle initials={editor.initials} />
                <div className="flex-1 min-w-0">
                  <h3 className="font-display text-navy font-semibold text-base leading-tight">
                    {editor.name}
                  </h3>
                  <p className="font-sans text-gray-600 text-xs mt-1.5 leading-relaxed">{editor.credentials}</p>
                  <a href={`mailto:${editor.email}`} className="inline-block mt-2 font-sans text-amber font-semibold text-xs hover:text-amber-dark transition-colors break-all">
                    {editor.email}
                  </a>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Managing Editor / Editorial Office */}
        <div className="mb-10">
          <SectionHead title="Managing Editor / Editorial Office" />
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
            {MANAGING_TEAM.map((person) => (
              <div
                key={person.id}
                className="bg-white rounded-lg border border-bg-border shadow-sm p-5 flex items-start gap-4"
              >
                <AvatarCircle initials={person.initials} photo={person.photo} name={person.name} />
                <div className="flex-1 min-w-0">
                  <span className="inline-block bg-bg-tag text-slate text-xs font-sans font-semibold px-2 py-0.5 rounded-full mb-2">
                    {person.role}
                  </span>
                  <h3 className="font-display text-navy font-semibold text-base leading-tight">
                    {person.name}
                  </h3>
                  {person.org && <p className="font-sans text-gray-500 text-xs mt-0.5">{person.org}</p>}
                  <a href={`mailto:${person.email}`} className="inline-block mt-2 font-sans text-amber font-semibold text-xs hover:text-amber-dark transition-colors break-all">
                    {person.email}
                  </a>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Editorial Policy & Independence */}
        <div className="bg-white rounded-lg border border-bg-border shadow-sm p-6 md:p-8 mb-10">
          <SectionHead title="Editorial Policy & Independence" />
          <p className="font-sans text-gray-700 text-sm leading-relaxed">
            The Editorial Board of <em>JAMSD</em> operates independently of its publisher. All editorial
            decisions are based solely on academic merit and relevance to the journal&#x2019;s scope.
            Conflicts of interest are declared and managed in accordance with COPE guidelines.
          </p>
        </div>

        {/* Join editorial board CTA */}
        <div className="bg-bg-ice rounded-lg border border-bg-border p-6 md:p-8 text-center">
          <h3 className="font-display text-navy text-xl font-bold mb-3">
            Interested in Joining the Editorial Board?
          </h3>
          <p className="font-sans text-gray-600 text-sm md:text-base leading-relaxed mb-5 max-w-xl mx-auto">
            JAMSD is seeking experienced clinicians and researchers to join our editorial team. If you are
            interested in serving as a reviewer or associate editor, please contact us.
          </p>
          <a
            href="mailto:editor@jamsd.com"
            className="btn-amber inline-flex items-center gap-2"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
            </svg>
            Contact Editorial Office
          </a>
        </div>
      </div>
    </>
  );
}

export default EditorialBoard;
